<!-- src/views/admin/AdminHealthSafetyManageFormOptionsView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <!-- PREMIUM HEADER -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 no-print">
      <div class="bg-[#0A1630] rounded-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <ShieldCheckIcon class="h-32 w-32 text-white" />
        </div>
        
        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">H&S Registry</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Registry Logic • Phase 20 Sequential Alignment</p>
          </div>
          
          <router-link :to="{ name: 'AdminHealthSafetyDashboard' }" class="bg-white/5 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
            <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return to H&S</span>
          </router-link>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- LOADING STATE -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <svg class="animate-spin h-10 w-10 text-[#20FCF1]" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="micro-label mt-4">Hydrating H&S Protocol Data...</p>
      </div>

      <div v-else-if="error" class="bg-rose-50 border border-rose-100 p-10 rounded-[40px] text-center shadow-sm">
        <ExclamationTriangleIcon class="h-10 w-10 text-rose-500 mx-auto mb-4" />
        <p class="text-rose-600 font-bold mb-6 uppercase tracking-widest text-[10px]">{{ error }}</p>
        <button @click="fetchFormOptions" class="bg-[#0A1630] text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all">Retry Connection</button>
      </div>
      
      <div v-else class="space-y-12">
        <!-- Loop through FORM SECTIONS -->
        <div v-for="section in formSections" :key="section.title" class="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden animate-fadeIn">
          <div class="bg-slate-50 border-b border-slate-100 p-8">
            <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">{{ section.title }}</h2>
          </div>
          
          <div class="p-8 space-y-16">
            <!-- Loop through OPTION GROUPS -->
            <div v-for="optionGroupId in section.optionGroupIds" :key="optionGroupId">
              
              <div v-if="rawOptionsData[optionGroupId]">
                <!-- Group Header -->
                <div class="flex justify-between items-end mb-6 border-b border-slate-100 pb-4">
                  <div>
                    <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">{{ formatTitle(optionGroupId) }}</h3>
                    <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Registry Reference: {{ optionGroupId }}</p>
                  </div>
                  <div v-if="clientHasOverrides[optionGroupId]" class="flex items-center gap-2">
                    <span class="text-[10px] font-black text-[#20FCF1] bg-[#EBFDF8] px-3 py-1 rounded-full uppercase tracking-widest">Client Specific</span>
                    <button @click="revertToGlobal(optionGroupId)" class="text-[10px] font-black text-rose-500 hover:underline uppercase tracking-widest">Reset</button>
                  </div>
                  <span v-else class="text-[10px] font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-widest">Global Protocol</span>
                </div>

                <!-- Option List -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  <div v-for="(option, index) in displayOptions[optionGroupId]" :key="index" class="group flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#20FCF1] transition-all shadow-sm">
                    <div class="flex flex-col">
                      <span class="text-[9px] font-mono text-slate-400 uppercase tracking-tighter">{{ option.value }}</span>
                      <span class="text-xs font-bold text-[#0A1630] uppercase tracking-tight">{{ option.text }}</span>
                    </div>
                    <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button @click="openEditModal(optionGroupId, option)" class="p-2 text-slate-400 hover:text-[#0A1630] transition-colors">
                        <PencilSquareIcon class="h-4 w-4" />
                      </button>
                      <button @click="handleDeleteOption(optionGroupId, option)" class="p-2 text-slate-400 hover:text-rose-500 transition-colors">
                        <TrashIcon class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div v-if="!displayOptions[optionGroupId]?.length" class="col-span-full py-12 text-center border-2 border-dashed border-slate-100 rounded-[32px]">
                    <p class="micro-label text-slate-300">No protocol entries defined in registry</p>
                  </div>
                </div>

                <!-- Dark Add Form -->
                <div class="bg-slate-900 rounded-[32px] p-8 shadow-2xl">
                  <form @submit.prevent="handleAddOption(optionGroupId)" class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label class="block text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] mb-3 ml-2">Internal Value</label>
                      <input 
                        type="text" 
                        v-model="newOptions[optionGroupId].value" 
                        placeholder="e.g. key_value" 
                        required 
                        class="premium-dark-input" 
                      />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] mb-3 ml-2">Display Label</label>
                      <input 
                        type="text" 
                        v-model="newOptions[optionGroupId].text" 
                        :placeholder="optionGroupId === 'investigationSystemicFactors' ? 'Prefix: Label' : 'e.g. Display Label'" 
                        required 
                        class="premium-dark-input" 
                      />
                    </div>
                    <div class="flex items-end">
                      <button type="submit" :disabled="isProcessing" class="w-full h-[54px] bg-[#20FCF1] text-[#0A1630] rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 shadow-lg">
                        {{ isProcessing ? 'Syncing...' : 'Add To Protocol' }}
                      </button>
                    </div>
                  </form>
                  <p v-if="optionGroupId === 'investigationSystemicFactors'" class="text-[9px] font-black text-[#20FCF1]/50 uppercase tracking-[0.2em] mt-4 ml-2">
                    🛡️ Systemic Mapping: Use 'Planning', 'Competence', 'Supervision', 'Resources' or 'Rail' as prefixes.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <TransitionRoot as="template" :show="isEditModalOpen">
      <Dialog as="div" class="relative z-50" @close="closeModal">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-[#0A1630]/80 backdrop-blur-sm transition-opacity" />
        </TransitionChild>
        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative transform overflow-hidden rounded-[40px] bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <form @submit.prevent="handleUpdateOption">
                  <div class="bg-white p-12">
                    <DialogTitle as="h3" class="text-2xl font-black text-[#0A1630] uppercase tracking-tight mb-8">Modify Entry</DialogTitle>
                    <div class="space-y-6">
                       <div>
                          <label class="micro-label mb-2">Technical Key (Immutable)</label>
                          <input type="text" :value="editingOption.current.value" disabled class="premium-input bg-slate-50 text-slate-400 cursor-not-allowed" />
                      </div>
                      <div>
                          <label class="micro-label mb-2">Public Label</label>
                          <input type="text" v-model="editingOption.newText" required class="premium-input font-bold" />
                      </div>
                    </div>
                  </div>
                  <div class="bg-slate-50 p-10 flex flex-col sm:flex-row-reverse gap-4">
                    <button type="submit" :disabled="isProcessing" class="bg-[#20FCF1] text-[#0A1630] px-12 py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:scale-105 transition-all">
                      {{ isProcessing ? 'Synchronising...' : 'Update Protocol' }}
                    </button>
                    <button type="button" class="text-[10px] font-black text-slate-400 uppercase tracking-widest px-8" @click="closeModal">Cancel</button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '@/composables/useAuth';
import { useUserStore } from '@/stores/userStore';
import { PencilSquareIcon, TrashIcon, ArrowUturnLeftIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';

const { claims } = useAuth();
const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2');
const manageFormOptions = httpsCallable(functions, 'manageFormOptions');

const loading = ref(true);
const error = ref(null);
const isProcessing = ref(false);
const rawOptionsData = ref({});
const isEditModalOpen = ref(false);

const newOptions = ref({});
const editingOption = reactive({
  sectionId: null,
  current: { text: '', value: '' },
  newText: ''
});

/**
 * PHASE 20 ALIGNMENT: CORE SECTIONS
 * Refined to align with the Investigation Form restructuring.
 * Environmental factors purged as per command.
 */
const formSections = ref([
  { 
    title: '1. Observation Form Protocols', 
    optionGroupIds: [
      'observationTypes', 'reportedByOptions', 'observationCategories', 'likelihoodOptions', 'severityOptions'
    ]
  },
  {
    title: '2. Accident Report Metrics',
    optionGroupIds: [
      'accidentTypes', 'injuryTypes', 'bodyParts'
    ]
  },
  {
    title: '3. Accident Witness Statement Support',
    optionGroupIds: [
      'witnessJobTitles', 'immediateManagerOptions'
    ]
  },
  {
    title: '4. H&S Investigation Registry Logic',
    optionGroupIds: [
      'investigationHumanFactors', 'investigationTechnicalFactors', 'investigationSystemicFactors'
    ]
  },
  {
    title: '5. Worksite Inspection Logic',
    optionGroupIds: [
      'inspectionPurposeOptions', 'typeOfProtectionOptions'
    ]
  }
]);

const formatTitle = (id) => {
    const map = {
        'observationTypes': 'Incident Classifications',
        'reportedByOptions': 'Reporter Entities',
        'observationCategories': 'H&S Categories',
        'likelihoodOptions': 'Risk Likelihood',
        'severityOptions': 'Impact Severity',
        'witnessJobTitles': 'Witness Roles',
        'immediateManagerOptions': 'Authorising Managers',
        'inspectionPurposeOptions': 'Inspection Drivers',
        'typeOfProtectionOptions': 'Safe System Protection',
        'investigationHumanFactors': 'Human Factors (Subject Behavior)',
        'investigationTechnicalFactors': 'Technical Factors (Equipment)',
        'investigationSystemicFactors': 'Systemic & Management Factors'
    };
    return map[id] || id.replace(/([A-Z])/g, " $1").toUpperCase();
};

const allGroupIds = [...new Set(formSections.value.flatMap(s => s.optionGroupIds))];
allGroupIds.forEach(id => {
  if (!newOptions.value[id]) newOptions.value[id] = { value: '', text: '' };
});

/** 🛡️ UPDATED SEEDS FOR SYSTEMIC SUB-HEADERS */
const defaultData = {
    observationTypes: [{ text: 'Accident', value: 'accident' }, { text: 'Close Call', value: 'close_call' }, { text: 'Good Practice', value: 'good_practice' }, { text: 'Incident', value: 'incident' }, { text: 'Asset Drainage', value: 'asset_drainage' }, { text: 'Other', value: 'other' }],
    reportedByOptions: [{ text: 'Client', value: 'client' }, { text: 'Manager', value: 'manager' }, { text: 'Operative', value: 'operative' }, { text: 'Public', value: 'public' }, { text: 'Supervisor', value: 'supervisor' }],
    observationCategories: [{ text: 'Chemicals/Hazardous', value: 'chemicals_hazardous' }, { text: 'Electrical Safety', value: 'electrical_safety' }, { text: 'Environmental', value: 'environmental' }, { text: 'Fatigue', value: 'fatigue' }, { text: 'Fire Safety', value: 'fire' }, { text: 'Plant & Machinery', value: 'plant' }, { text: 'PPE', value: 'ppe' }, { text: 'SSoW Planning', value: 'ssow' }, { text: 'Work At Height', value: 'height' }],
    likelihoodOptions: [{ text: 'Certain', value: 'certain' }, { text: 'Highly Unlikely', value: 'highly_unlikely' }, { text: 'Likely', value: 'likely' }, { text: 'Possible', value: 'possible' }, { text: 'Unlikely', value: 'unlikely' }],
    severityOptions: [{ text: 'Fatality', value: 'fatality' }, { text: 'Lost Time Injury', value: 'lost_time' }, { text: 'Major Injury', value: 'major' }, { text: 'Minor Injury', value: 'minor' }, { text: 'No Injury', value: 'none' }],
    witnessJobTitles: [{ text: 'Director', value: 'director' }, { text: 'Engineering Manager', value: 'eng_mgr' }, { text: 'HSQE Manager', value: 'hsqe_mgr' }, { text: 'Supervisor', value: 'supervisor' }, { text: 'Operative', value: 'operative' }],
    accidentTypes: [{ text: 'Slip / Trip / Fall', value: 'stf' }, { text: 'Manual Handling', value: 'manual' }, { text: 'Struck by Object', value: 'struck' }, { text: 'Vehicle Incident', value: 'vehicle' }],
    injuryTypes: [{ text: 'Cut / Laceration', value: 'cut' }, { text: 'Bruise', value: 'bruise' }, { text: 'Fracture', value: 'fracture' }, { text: 'Burn', value: 'burn' }],
    bodyParts: [{ text: 'Head', value: 'head' }, { text: 'Arm', value: 'arm' }, { text: 'Back', value: 'back' }, { text: 'Leg', value: 'leg' }],
    inspectionPurposeOptions: [{ text: 'Planned', value: 'planned' }, { text: 'Unscheduled', value: 'unscheduled' }, { text: 'Post-Incident', value: 'post_incident' }],
    typeOfProtectionOptions: [{ text: 'ALoP', value: 'alop' }, { text: 'T-ALoP', value: 't_alop' }, { text: 'Line Blockage', value: 'line_blockage' }, { text: 'Possession', value: 'possession' }],
    investigationHumanFactors: [{ text: 'Fatigue/Alertness', value: 'fatigue' }, { text: 'SOP Breach', value: 'sop_breach' }],
    investigationTechnicalFactors: [{ text: 'Equipment Failure', value: 'failure' }, { text: 'Tooling Issue', value: 'tooling' }],
    investigationSystemicFactors: [
      { text: 'Planning: Risk Assessment Error', value: 'plan_ra' }, 
      { text: 'Competence: Training Gap', value: 'comp_train' },
      { text: 'Supervision: Monitoring Lapse', value: 'super_lapse' },
      { text: 'Resources: Standard Deviation', value: 'res_dev' },
      { text: 'Rail: Specific Site Violation', value: 'rail_spec' }
    ]
};

const clientHasOverrides = computed(() => {
  const result = {};
  if (!claims.value?.clientId) return result;
  allGroupIds.forEach(id => {
    result[id] = rawOptionsData.value[id] && rawOptionsData.value[id][claims.value.clientId] !== undefined;
  });
  return result;
});

const displayOptions = computed(() => {
  const result = {};
  const clientId = claims.value?.clientId;
  allGroupIds.forEach(id => {
    const data = rawOptionsData.value[id];
    if (data) {
      result[id] = (clientId && data[clientId]) ? data[clientId] : (data.global || []);
    } else {
      result[id] = [];
    }
  });
  return result;
});

const fetchManagersFromDB = async () => {
    try {
        const userSnaps = await getDocs(collection(db, 'userProfiles'));
        const managers = [];
        userSnaps.forEach(doc => {
            const u = doc.data();
            const job = (u.jobTitle || '').toLowerCase();
            if (job.includes('manager') || job.includes('director')) {
                const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
                if (name) managers.push({ text: name, value: name.toLowerCase().replace(/\s+/g, '_') });
            }
        });
        return [...new Map(managers.map(item => [item['value'], item])).values()].sort((a,b) => a.text.localeCompare(b.text));
    } catch(e) { return []; }
};

const fetchFormOptions = async () => {
  if (!claims.value?.clientId) return;
  loading.value = true;
  try {
    const docSnaps = await Promise.all(allGroupIds.map(id => getDoc(doc(db, 'formOptions', id))));
    const newOptionsData = {};
    const seedPromises = [];
    const dbManagers = await fetchManagersFromDB();

    docSnaps.forEach((docSnap, index) => {
      const sectionId = allGroupIds[index];
      let sectionDefault = defaultData[sectionId];
      if (sectionId === 'immediateManagerOptions' && dbManagers.length > 0) sectionDefault = dbManagers;

      if (docSnap.exists()) {
        const data = docSnap.data();
        if ((!data.global || data.global.length === 0) && sectionDefault) {
            seedPromises.push(setDoc(doc(db, 'formOptions', sectionId), { global: sectionDefault }, { merge: true }));
            newOptionsData[sectionId] = { global: sectionDefault, ...data };
        } else {
            newOptionsData[sectionId] = data;
        }
      } else if (sectionDefault) {
          seedPromises.push(setDoc(doc(db, 'formOptions', sectionId), { global: sectionDefault }));
          newOptionsData[sectionId] = { global: sectionDefault };
      } else {
          newOptionsData[sectionId] = { global: [] };
      }
    });

    if (seedPromises.length > 0) await Promise.all(seedPromises);
    rawOptionsData.value = newOptionsData;
  } catch (err) {
    error.value = "Failed to load protocol registry.";
  } finally {
    loading.value = false;
  }
};

watch(() => claims.value, (newClaims) => {
  if (newClaims?.clientId) fetchFormOptions();
}, { immediate: true });

const getSanitizedList = (id) => {
    const list = clientHasOverrides.value[id] ? rawOptionsData.value[id][claims.value.clientId] : displayOptions.value[id];
    return JSON.parse(JSON.stringify(list || []));
};

const handleAddOption = async (sectionId) => {
  const input = newOptions.value[sectionId];
  if (!input.value || !input.text) return;
  isProcessing.value = true;
  const payload = { value: input.value.trim().toLowerCase().replace(/\s+/g, '_'), text: input.text.trim() };
  try {
    if (userStore.isAdmin) {
        const currentList = getSanitizedList(sectionId);
        currentList.push(payload);
        await setDoc(doc(db, 'formOptions', sectionId), { [claims.value.clientId]: currentList }, { merge: true });
    } else {
        await manageFormOptions({ action: 'add', sectionId, clientId: claims.value.clientId, option: payload });
    }
    newOptions.value[sectionId] = { value: '', text: '' };
    await fetchFormOptions();
  } catch (err) {
    alert(`Error: ${err.message}`);
  } finally {
    isProcessing.value = false;
  }
};

const handleUpdateOption = async () => {
  const { sectionId, current, newText } = editingOption;
  const updated = { text: newText.trim(), value: current.value };
  isProcessing.value = true;
  try {
    if (userStore.isAdmin) {
        const list = getSanitizedList(sectionId);
        const idx = list.findIndex(o => o.value === current.value);
        if (idx !== -1) list[idx] = updated;
        await setDoc(doc(db, 'formOptions', sectionId), { [claims.value.clientId]: list }, { merge: true });
    } else {
        await manageFormOptions({ action: 'remove', sectionId, clientId: claims.value.clientId, option: current });
        await manageFormOptions({ action: 'add', sectionId, clientId: claims.value.clientId, option: updated });
    }
    await fetchFormOptions();
    isEditModalOpen.value = false;
  } catch (err) {
    alert(`Error: ${err.message}`);
  } finally {
    isProcessing.value = false;
  }
};

const handleDeleteOption = async (sectionId, option) => {
  if (!confirm('Authorise permanent removal of this protocol entry?')) return;
  isProcessing.value = true;
  try {
    if (userStore.isAdmin) {
        const list = getSanitizedList(sectionId);
        const newList = list.filter(o => o.value !== option.value);
        await setDoc(doc(db, 'formOptions', sectionId), { [claims.value.clientId]: newList }, { merge: true });
    } else {
        await manageFormOptions({ action: 'remove', sectionId, clientId: claims.value.clientId, option });
    }
    await fetchFormOptions();
  } catch (err) {
    alert(`Error: ${err.message}`);
  } finally {
    isProcessing.value = false;
  }
};

const revertToGlobal = async (sectionId) => {
    if (!confirm('Reset to global defaults? This will erase all client customisations.')) return;
    isProcessing.value = true;
    try {
        await manageFormOptions({ action: 'reset', sectionId, clientId: claims.value.clientId });
        await fetchFormOptions();
    } catch (err) {
        alert(`Error: ${err.message}`);
    } finally {
        isProcessing.value = false;
    }
};

const openEditModal = (id, opt) => {
    editingOption.sectionId = id;
    editingOption.current = opt;
    editingOption.newText = opt.text;
    isEditModalOpen.value = true;
};
const closeModal = () => { isEditModalOpen.value = false; };
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.micro-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }
.premium-input { @apply mt-1 block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-[#0A1630] shadow-sm focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.premium-dark-input { @apply block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.font-mono { font-family: ui-monospace, SFMono-Regular, Consolas, "Liberation Mono", monospace; }
</style>